import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { getArticles, getCategories, buildImageUrl } from "../../lib/strapi";
import { ListSkeleton } from "../../ui/Skeletons";
import CategoryBadge from "../../ui/CategoryBadge";
import AuthorBadge from "../../ui/AuthorBadge";
import Pagination from "../../ui/Pagination";

const pick = (item) => (item && item.attributes ? item.attributes : item || {});
const pickMedia = (m) => (m && m.data && m.data.attributes ? m.data.attributes : m || {});

export default function Blog() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [posts, setPosts] = useState([]);
  const [meta, setMeta] = useState(null);
  const [categories, setCategories] = useState([]);

  const page = Number(searchParams.get("page") || 1);
  const search = searchParams.get("q") || "";
  const category = searchParams.get("category") || "";

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const [{ data, meta }, cats] = await Promise.all([
          getArticles({
            page,
            pageSize: 9,
            categorySlug: category,
            search,
            // using '*' makes sure cover/category/author are present regardless of Strapi version
            populate: "*",
          }),
          categories.length ? Promise.resolve(categories) : getCategories(),
        ]);
        if (!mounted) return;
        setPosts(Array.isArray(data) ? data : []);
        setMeta(meta || null);
        if (!categories.length) setCategories(cats);
      } catch (err) {
        console.error(err);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, category, search]);

  const onSearch = (e) => {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const q = form.get("q")?.toString() || "";
    const next = new URLSearchParams(searchParams);
    if (q) next.set("q", q);
    else next.delete("q");
    next.set("page", "1");
    setSearchParams(next);
  };

  const onCategoryChange = (slug) => {
    const next = new URLSearchParams(searchParams);
    if (slug) next.set("category", slug);
    else next.delete("category");
    next.set("page", "1");
    setSearchParams(next);
  };

  const currentPage = meta?.pagination?.page || 1;
  const pageCount = meta?.pagination?.pageCount || 1;

  return (
    <main className="mx-auto max-w-6xl px-4 py-10">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-8">
        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight">Help Articles</h1>

        <form onSubmit={onSearch} className="flex w-full md:w-auto items-center gap-2">
          <input
            name="q"
            defaultValue={search}
            className="w-full md:w-80 rounded-xl border border-gray-300 bg-white/70 px-4 py-2 outline-none focus:ring-2 focus:ring-black"
            placeholder="Search posts…"
          />
          <button className="rounded-xl border border-gray-900 px-4 py-2 font-semibold hover:bg-gray-900 hover:text-white transition">
            Search
          </button>
        </form>
      </div>

      {/* Category pills */}
      <div className="flex flex-wrap gap-2 mb-8">
        <button
          onClick={() => onCategoryChange("")}
          className={`px-3 py-1.5 rounded-full border text-sm transition ${
            !category ? "bg-black text-white border-black" : "border-gray-300 hover:border-black"
          }`}
        >
          All
        </button>
        {categories.map((c) => {
          const ca = pick(c);
          const cslug = ca.slug || ca?.attributes?.slug;
          const cname = ca.name || ca.title || cslug;
          return (
            <button
              key={c.id || c.documentId || cname}
              onClick={() => onCategoryChange(cslug)}
              className={`px-3 py-1.5 rounded-full border text-sm transition ${
                category === cslug ? "bg-black text-white border-black" : "border-gray-300 hover:border-black"
              }`}
            >
              {cname}
            </button>
          );
        })}
      </div>

      {loading ? (
        <ListSkeleton />
      ) : posts.length === 0 ? (
        <p className="text-gray-500">No posts found.</p>
      ) : (
        <>
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {posts.map((post) => {
              const a = pick(post); // v4 or v5
              const cover = pickMedia(a.cover);
              const author =
                pick(a.author) || null; // null in your JSON, so badge will hide
              const categoryRel = pick(a.category) || null;

              const authorName =
                author?.name || author?.username || null;

              const categoryName =
                categoryRel?.name || categoryRel?.title || categoryRel?.slug || null;

              return (
                <li
                  key={post.id || a.documentId}
                  className="group border rounded-2xl overflow-hidden bg-white shadow-sm hover:shadow-md transition"
                >
                  <Link to={`/help/${a.slug}`} className="block">
                    <div className="aspect-[16/10] overflow-hidden bg-gray-100">
                      {cover?.url ? (
                        <img
                          src={buildImageUrl(cover)}
                          alt={a.title || "Article cover"}
                          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                          loading="lazy"
                        />
                      ) : (
                        <div className="h-full w-full" />
                      )}
                    </div>

                    <div className="p-5">
                      <div className="flex items-center gap-2 mb-3">
                        {categoryName ? <CategoryBadge>{categoryName}</CategoryBadge> : null}
                        {authorName ? <AuthorBadge>{authorName}</AuthorBadge> : null}
                      </div>
                      <h2 className="text-xl font-bold leading-snug group-hover:underline">
                        {a.title}
                      </h2>
                      {a.description && (
                        <p className="mt-2 text-sm text-gray-600 line-clamp-3">{a.description}</p>
                      )}
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>

          <div className="mt-10 flex justify-center">
            <Pagination
              currentPage={currentPage}
              pageCount={pageCount}
              onPageChange={(p) => {
                const next = new URLSearchParams(searchParams);
                next.set("page", String(p));
                setSearchParams(next);
              }}
            />
          </div>
        </>
      )}
    </main>
  );
}
